import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  ArrowLeft,
  Cpu,
  CreditCard,
  TrendingUp,
  MapPin,
  User,
  Mail,
  Phone,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import LeaderboardView from '../components/LeaderboardView';

const GymDetail = () => {
  const { id } = useParams();
  const [gym, setGym] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('members');

  const [isEditingSerial, setIsEditingSerial] = useState(false);
  const [editSerialVal, setEditSerialVal] = useState('');
  const [serialError, setSerialError] = useState('');
  const [serialSaving, setSerialSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const fetchGymDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');

      // 1. Fetch main gym metadata
      const resGym = await fetch(`${API_BASE_URL}/api/admin/gyms/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!resGym.ok) throw new Error('Failed to retrieve gym details.');
      const dataGym = await resGym.json();

      // 3. Fetch gym's members (we can query the generic gym member data endpoint)
      const resMembers = await fetch(`${API_BASE_URL}/api/gym/Gymuserdata`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gymcode: dataGym.data.code })
      });
      const dataMembers = resMembers.ok ? await resMembers.json() : null;

      if (dataGym.success) {
        setGym(dataGym.data);
        setEditSerialVal(dataGym.data.hardwareSerial || '');
      }
      if (dataMembers && dataMembers.allGymUsers) {
        setMembers(dataMembers.allGymUsers);
      } else {
        setMembers([]);
      }

    } catch (err) {
      console.error(err);
      setError('Could not fetch details from API. Displaying simulation details.');
      // Load fallback simulation data
      const mockGym = getMockGym();
      setGym(mockGym);
      setEditSerialVal(mockGym.hardwareSerial || '');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGymDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSaveSerial = async () => {
    setSerialSaving(true);
    setSerialError('');
    try {
      const token = localStorage.getItem('admin_token');
      const updatedSerial = editSerialVal.trim() === '' ? null : editSerialVal.trim();

      const updatePayload = {
        ...gym,
        hardwareSerial: updatedSerial
      };

      // Remove properties that are computed/extra on frontend but not allowed or needed by backend GymDetails DTO / entity
      delete updatePayload.membersCount;
      delete updatePayload.trainersCount;
      delete updatePayload.weeklyCheckIns;
      delete updatePayload.challengesActive;
      delete updatePayload.device;

      const res = await fetch(`${API_BASE_URL}/api/admin/gyms/${gym.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to update hardware serial.');
      }

      const resData = await res.json();
      if (resData.success) {
        setIsEditingSerial(false);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
        await fetchGymDetails();
      } else {
        throw new Error(resData.message || 'Failed to update hardware serial.');
      }
    } catch (err) {
      console.error(err);
      setSerialError(err.message || 'Error occurred while saving hardware serial.');
    } finally {
      setSerialSaving(false);
    }
  };

  const getMockGym = () => {
    return {
      id: id,
      code: 'MUS-1029',
      name: 'Muscle Factory',
      phonenumber: '9876543210',
      ownerUsername: 'rajesh123',
      ownerName: 'Rajesh Kumar',
      ownerEmail: 'rajesh@gmail.com',
      ownerVpa: 'rajesh@upi',
      address: 'Madhapur, Hyderabad',
      openHours: '6:00 AM - 10:00 PM',
      logoUrl: null,
      active: true,
      planTier: 'pro',
      status: 'active',
      trialEndsAt: null,
      hardwareSerial: 'FS-001',
      internalNotes: 'VIP partner. Primary feedback source for Hyderabad region pilot.',
      membersCount: 145,
      trainersCount: 4,
      weeklyCheckIns: 184,
      challengesActive: 48
    };
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 text-[#6CD800]">
        <RefreshCw size={24} className="animate-spin mr-2" />
        Loading gym analysis portfolio...
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="text-center text-gray-400 py-12">
        <AlertTriangle size={32} className="mx-auto text-red-400 mb-2" />
        Gym workspace profile not found.
        <Link to="/admin/gyms" className="block text-[#6CD800] text-sm hover:underline mt-2">Back to roster</Link>
      </div>
    );
  }

  // Evaluate connection status of the hardware
  const isDeviceOnline = gym.device?.lastSeenAt && (new Date().getTime() - new Date(gym.device.lastSeenAt).getTime()) < 30 * 60 * 1000;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Back link */}
      <div>
        <Link to="/admin/gyms" className="inline-flex items-center text-xs font-semibold text-gray-400 hover:text-white uppercase tracking-wider">
          <ArrowLeft size={14} className="mr-1" /> Back to Gym Roster
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-2 gap-4">
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-white tracking-tight">{gym.name}</h1>
            <span className="text-[#6CD800] bg-[#6CD800]/10 border border-[#6CD800]/25 px-2 py-0.5 rounded text-xs font-mono font-bold tracking-wider">{gym.code}</span>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold w-fit ${gym.status === 'active'
            ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20'
            : gym.status === 'trial'
              ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
              : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
            <span className={`h-1.5 w-1.5 rounded-full mr-2 ${gym.status === 'active' ? 'bg-[#6CD800]' : gym.status === 'trial' ? 'bg-yellow-400' : 'bg-red-500'
              }`}></span>
            {gym.status.toUpperCase()}
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4">
          {error}
        </div>
      )}

      {/* Summary KPI Panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg">
        <div className="space-y-1.5 border-r border-[#1E293B] last:border-none pr-4">
          <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider block">Total Members</span>
          <span className="text-2xl font-bold text-white block">{gym.membersCount}</span>
          <span className="text-xs text-gray-500 block">Registered users</span>
        </div>
        <div className="space-y-1.5 border-r border-[#1E293B] last:border-none pr-4">
          <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider block">Active Trainers</span>
          <span className="text-2xl font-bold text-white block">{gym.trainersCount}</span>
          <span className="text-xs text-gray-500 block">Gym employees</span>
        </div>
        <div className="space-y-1.5 border-r border-[#1E293B] last:border-none pr-4">
          <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider block">Check-ins (Weekly)</span>
          <span className="text-2xl font-bold text-white block">{gym.weeklyCheckIns}</span>
          <span className="text-xs text-gray-500 block">This week's volume</span>
        </div>
        <div className="space-y-1.5 last:border-none">
          <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider block">Streaking Challenges</span>
          <span className="text-2xl font-bold text-white block">{gym.challengesActive}</span>
          <span className="text-xs text-gray-500 block">Active participations</span>
        </div>
      </div>

      {/* Main Grid: Tabs and Info Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Left Side Tab Navigation and Contents */}
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg lg:col-span-2 overflow-hidden flex flex-col">
          {/* Tab headers */}
          <div className="flex border-b border-[#1E293B] bg-[#0B0F19]">
            <button
              onClick={() => setActiveTab('members')}
              className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${activeTab === 'members'
                ? 'border-[#6CD800] text-[#6CD800] bg-[#1E293B]/20'
                : 'border-transparent text-gray-400 hover:text-white'
                }`}
            >
              Member Directory
            </button>
            <button
              onClick={() => setActiveTab('hardware')}
              className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${activeTab === 'hardware'
                ? 'border-[#6CD800] text-[#6CD800] bg-[#1E293B]/20'
                : 'border-transparent text-gray-400 hover:text-white'
                }`}
            >
              Hardware Unit
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${activeTab === 'subscription'
                ? 'border-[#6CD800] text-[#6CD800] bg-[#1E293B]/20'
                : 'border-transparent text-gray-400 hover:text-white'
                }`}
            >
              Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${activeTab === 'leaderboard'
                ? 'border-[#6CD800] text-[#6CD800] bg-[#1E293B]/20'
                : 'border-transparent text-gray-400 hover:text-white'
                }`}
            >
              Leaderboard
            </button>
          </div>

          {/* Tab Body */}
          <div className="p-6 flex-1">

            {/* Tab 1: Member Directory */}
            {activeTab === 'members' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Onboarded Members (Read-Only)</h3>
                  <span className="text-xs text-gray-400">{members.length} Total Users</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-gray-300">
                    <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
                      <tr>
                        <th scope="col" className="px-4 py-3">Member Name</th>
                        <th scope="col" className="px-4 py-3">Phone</th>
                        <th scope="col" className="px-4 py-3">Joined Date</th>
                        <th scope="col" className="px-4 py-3">Plan Name</th>
                        <th scope="col" className="px-4 py-3">Renewal Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1E293B]">
                      {members.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-6 text-gray-500">No active members registered at this gym.</td>
                        </tr>
                      ) : (
                        members.map((member, idx) => (
                          <tr key={idx} className="hover:bg-[#1E293B]/10">
                            <td className="px-4 py-3 font-semibold text-white">{member.firstName} {member.lastName}</td>
                            <td className="px-4 py-3 text-xs text-gray-400">{member.phoneNumber}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{new Date(member.userPlanJoiningDate).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-xs">
                              <span className="px-2 py-0.5 rounded font-mono uppercase bg-[#1E293B] text-gray-300 border border-gray-700">
                                {member.userplanname || 'Custom'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400">
                              {member.userPlanExpiryDate ? new Date(member.userPlanExpiryDate).toLocaleDateString() : 'Never'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tab 2: Hardware Status */}
            {activeTab === 'hardware' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Raspberry Pi QR Scanner</h3>
                  {!isEditingSerial && (
                    gym.hardwareSerial ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isDeviceOnline
                        ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20'
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>
                        {isDeviceOnline ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Unlinked</span>
                    )
                  )}
                </div>

                {isEditingSerial ? (
                  <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6 space-y-4 animate-slide-in">
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider">Configure Hardware Serial</h4>
                    <div className="space-y-2">
                      <label className="text-xs text-gray-400 block">Enter Raspberry Pi Serial ID (leave blank to unlink device)</label>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={editSerialVal}
                          onChange={(e) => setEditSerialVal(e.target.value)}
                          placeholder="e.g. FS-001"
                          className="flex-1 bg-[#0F172A] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#6CD800] transition-colors"
                          disabled={serialSaving}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveSerial}
                            disabled={serialSaving}
                            className="flex-1 sm:flex-initial px-4 py-2 bg-[#6CD800] hover:bg-[#5cbd00] disabled:bg-gray-600 text-black font-semibold rounded-lg text-xs transition-all flex items-center justify-center min-w-[70px]"
                          >
                            {serialSaving ? (
                              <>
                                <RefreshCw size={14} className="animate-spin mr-1.5" />
                                Saving...
                              </>
                            ) : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingSerial(false);
                              setSerialError('');
                            }}
                            disabled={serialSaving}
                            className="flex-1 sm:flex-initial px-4 py-2 bg-[#1E293B] hover:bg-gray-800 text-white font-semibold rounded-lg text-xs transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      {serialError && (
                        <p className="text-xs font-semibold text-red-400 mt-1">{serialError}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  gym.hardwareSerial ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <Cpu size={18} className="text-[#6CD800]" />
                          <div className="flex-1 flex justify-between items-center">
                            <div>
                              <span className="text-xs text-gray-500 block">Device Serial ID</span>
                              <span className="text-sm font-mono font-bold text-white">{gym.hardwareSerial}</span>
                            </div>
                            <button
                              onClick={() => {
                                setEditSerialVal(gym.hardwareSerial || '');
                                setIsEditingSerial(true);
                                setSerialError('');
                              }}
                              className="text-xs font-semibold text-[#6CD800] hover:text-[#5cbd00] transition-colors font-mono"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <TrendingUp size={18} className="text-blue-400" />
                          <div>
                            <span className="text-xs text-gray-500 block">Firmware Version</span>
                            <span className="text-sm font-bold text-white">{gym.device?.firmwareVersion || '1.0.0'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <RefreshCw size={18} className="text-yellow-400" />
                          <div>
                            <span className="text-xs text-gray-500 block">Last Seen Ping</span>
                            <span className="text-sm font-bold text-white">
                              {gym.device?.lastSeenAt ? new Date(gym.device.lastSeenAt).toLocaleString() : 'Never'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <AlertTriangle size={18} className={isDeviceOnline ? "text-green-400" : "text-red-400"} />
                          <div>
                            <span className="text-xs text-gray-500 block">Computed Status</span>
                            <span className="text-sm font-bold text-white">{isDeviceOnline ? 'Device is healthy (sending heartbeats)' : 'Device is inactive (offline for >30 mins)'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 border border-[#1E293B] border-dashed rounded-xl">
                      <Cpu size={32} className="mx-auto text-gray-600 mb-2" />
                      No Raspberry Pi device linked to this gym workspace profile yet.
                      <p className="text-xs text-gray-600 mt-1 mb-4">Onboard a device by modifying the gym's Hardware Serial setting.</p>
                      <button
                        onClick={() => {
                          setEditSerialVal('');
                          setIsEditingSerial(true);
                          setSerialError('');
                        }}
                        className="px-4 py-2 bg-[#6CD800] hover:bg-[#5cbd00] text-black font-semibold rounded-lg text-xs transition-colors"
                      >
                        Edit Hardware Serial
                      </button>
                    </div>
                  )
                )}
              </div>
            )}

            {/* Tab 3: Subscription & Plan details */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Subscription Matrix</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-[#0B0F19] border border-[#1E293B] rounded-lg p-5">
                    <span className="text-xs text-gray-500 block">Plan Tier</span>
                    <span className="text-lg font-mono font-bold text-[#6CD800] uppercase mt-1 block">{gym.planTier}</span>
                  </div>
                  <div className="bg-[#0B0F19] border border-[#1E293B] rounded-lg p-5">
                    <span className="text-xs text-gray-500 block">Subscription Status</span>
                    <span className="text-lg font-bold text-white uppercase mt-1 block">{gym.status}</span>
                  </div>
                  <div className="bg-[#0B0F19] border border-[#1E293B] rounded-lg p-5">
                    <span className="text-xs text-gray-500 block">Renewal / Expiry Date</span>
                    <span className="text-lg font-bold text-gray-400 mt-1 block">
                      {gym.trialEndsAt ? new Date(gym.trialEndsAt).toLocaleDateString() : 'N/A (Paid Tier)'}
                    </span>
                  </div>
                </div>

                <div className="border border-[#1E293B] rounded-xl p-5 bg-[#0B0F19]/40 space-y-3">
                  <h4 className="text-xs font-semibold text-white uppercase tracking-wider flex items-center">
                    <CreditCard size={14} className="mr-1.5 text-[#6CD800]" /> Billing & Gateway info
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-400">
                    <div>
                      <span className="block">Owner VPA (UPI):</span>
                      <span className="text-white font-mono">{gym.ownerVpa || 'Not configured'}</span>
                    </div>
                    <div>
                      <span className="block">Joining Date:</span>
                      <span className="text-white">{new Date(gym.joiningdate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 4: Consistency Leaderboard */}
            {activeTab === 'leaderboard' && (
              <LeaderboardView gymId={gym.id} isAdmin={true} />
            )}
          </div>
        </div>

        {/* Right Side: Owner info & Admin notes */}
        <div className="space-y-6">
          {/* Owner info card */}
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg space-y-6">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider border-b border-[#1E293B] pb-2">Gym Owner Profile</h3>

            <div className="space-y-4 text-sm text-gray-300">
              <div className="flex items-center space-x-3">
                <User size={16} className="text-[#6CD800]" />
                <div>
                  <span className="text-xs text-gray-500 block">Owner Name</span>
                  <span className="font-semibold text-white">{gym.ownerName}</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail size={16} className="text-[#6CD800]" />
                <div>
                  <span className="text-xs text-gray-500 block">Owner Email</span>
                  <span className="font-semibold text-white">{gym.ownerEmail}</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Phone size={16} className="text-[#6CD800]" />
                <div>
                  <span className="text-xs text-gray-500 block">Whatsapp-linked Phone</span>
                  <span className="font-semibold text-white">{gym.phonenumber}</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MapPin size={16} className="text-[#6CD800]" />
                <div>
                  <span className="text-xs text-gray-500 block">Address Location</span>
                  <span className="font-semibold text-white">{gym.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Internal notes card */}
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider border-b border-[#1E293B] pb-2">Administrative Notes</h3>
            <p className="text-sm text-gray-400 italic bg-[#0B0F19] p-4 rounded-lg border border-[#1E293B]">
              {gym.internalNotes || 'No administrative notes configured for this gym workspace profile.'}
            </p>
          </div>
        </div>

      </div>

      {/* Toast Notification */}
      {showSuccessToast && (
        <div className="fixed top-6 right-6 z-50 animate-slide-in flex items-center space-x-3 bg-slate-900/85 backdrop-blur-md border border-[#6CD800]/40 rounded-xl px-5 py-3.5 shadow-2xl shadow-[#6CD800]/20 max-w-sm transition-all duration-300">
          <div className="h-2.5 w-2.5 rounded-full bg-[#6CD800] animate-pulse" />
          <div className="flex-1">
            <h5 className="text-xs font-bold text-[#6CD800] uppercase tracking-wider">Device Assigned</h5>
            <p className="text-xs text-gray-300 mt-0.5 font-medium">Device updated successfully</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GymDetail;
